import { useEffect, useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router-dom";
import { PolicyReference } from "../../db/schema";
import Button from "../common/Button";
import { policiesApi, policyDocumentPath } from "../Policies/api";

interface PolicySectionViewProps {
  reference: PolicyReference;
  onBack: () => void;
}

// One policy section in the editor sidebar: opened from a policy suggestion's
// source badge. Back returns to the suggestions list; "View full document"
// opens the document at this section, and the document's own Back button
// brings the officer back here, to the report.
function PolicySectionView({ reference: r, onBack }: PolicySectionViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setContent(null);
    setError(false);
    policiesApi
      .section(r.documentId, r.sectionId)
      .then((d) => live && setContent(d.section.content))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [r.documentId, r.sectionId]);

  const pages = r.pageStart != null ? `p.${r.pageStart}${r.pageEnd != null && r.pageEnd !== r.pageStart ? `-${r.pageEnd}` : ""}` : null;

  return (
    <div className="pt-2 md:pt-0" data-tour="policy-section">
      <Button variant="clean" size="xs" className="px-1.5 mb-2 hover:bg-base-300" onClick={onBack}>
        <ArrowLeftIcon className="size-4 mr-1" /> Back to suggestions
      </Button>
      <div className="p-4 bg-base-200 rounded-lg">
        <p className="text-[11px] text-base-content/60 mb-1">
          {r.documentTitle}
          {pages ? ` · ${pages}` : ""}
        </p>
        <h4 className="text-sm font-semibold mb-2">
          {r.sectionNumber ? `§ ${r.sectionNumber}` : ""}
          {r.sectionNumber && r.heading ? " " : ""}
          {r.heading || (!r.sectionNumber ? r.documentTitle : "")}
        </h4>
        {error ? (
          <p className="text-sm text-base-content/70">That section is no longer available.</p>
        ) : content === null ? (
          <Skeleton count={6} height={14} />
        ) : (
          <p className="text-xs 2xl:text-sm text-gray-600 dark:text-base-content/70 whitespace-pre-line">{content}</p>
        )}
      </div>
      <Button
        variant="primary"
        size="sm"
        className="w-full mt-3"
        onClick={() =>
          navigate(policyDocumentPath(r.documentId, { page: r.pageStart, sectionId: r.sectionId }), {
            state: { fromEditor: true, from: location.pathname + location.search },
          })
        }
      >
        View full document
      </Button>
    </div>
  );
}

export default PolicySectionView;
